/**
 * SAPIENT alert presentation helpers (REQ-UI-062).
 *
 * The Alerts panel rows — and (PR5b) the Map alert-pin tooltip + the
 * (deferred) Flow-F notification surface — render an alert's priority
 * badge, type, confidence, and age, so the null-aware / total formatting
 * lives here once (CLAUDE.md §4.4 — no duplicated logic) rather than
 * inline in each consumer. The Detections analogue is
 * :file:`detections/detection-display.ts`.
 *
 * # Priority → severity → colour (ADR-029 §5)
 *
 * The wire carries ``priority`` as the SAPIENT ``AlertPriority`` enum
 * integer (``schemas/proto/meridian/v1/data/alert.proto`` §AlertPriority):
 * 0 UNSPECIFIED / 1 LOW / 2 MEDIUM / 3 HIGH / 4 CRITICAL. Per the ADR-029
 * §5 "BSI → Flow F" severity table:
 *
 *   | priority    | severity   | Flow-F surface                  |
 *   | :---------- | :--------- | :------------------------------ |
 *   | UNSPECIFIED | ``info``     | toast (auto-dismiss 6s)        |
 *   | LOW         | ``info``     | toast (auto-dismiss 6s)        |
 *   | MEDIUM      | ``warning``  | toast (auto-dismiss 6s)        |
 *   | HIGH        | ``error``    | toast (operator-dismissed)     |
 *   | CRITICAL    | ``critical`` | sticky banner (persistent)     |
 *
 * Rather than introduce a parallel severity-colour token set, the badge
 * reuses the EXISTING shared marker-status palette
 * (:func:`map/marker-styling.getStatusColor`, via the ``markerStatus``
 * field) so alert badges, event chips, and the Map's health markers all
 * draw from the same Appaloosa status tokens (REQ-UI-046 — colour
 * survives both themes). The severity → swatch mapping follows the Events
 * convention (low = grey, medium = amber, high/critical = red): ``info``
 * → ``unknown`` (grey); ``warning`` → ``degraded`` (amber); ``error`` +
 * ``critical`` → ``unhealthy`` (red). CRITICAL folds into the red swatch
 * (the status palette has no distinct critical colour) while keeping its
 * own label, severity, and sticky-banner Flow-F surface.
 *
 * All helpers are pure + total — they never throw, returning a neutral /
 * placeholder value for the absent/null/unknown case so the row always
 * has something legible to show (long-retention forward-compatibility,
 * CLAUDE.md §8.3).
 *
 * Cross-references:
 *   - schemas/proto/meridian/v1/data/alert.proto §AlertPriority / §AlertType.
 *   - ADR-029 §5 (severity mapping) / §6 (operator UI surfaces).
 *   - map/marker-styling.ts (the shared status palette).
 *   - REQ-UI-046 (status-colour legibility) / REQ-UI-062 (Alerts panel).
 *   - REQ-INGEST-044 (nullable confidence).
 */
import type { Role } from "@/auth/session-types";
import type { MarkerStatus } from "@/map/marker-styling";

/** The em-dash shown for a null/absent display field. */
export const ALERT_DISPLAY_PLACEHOLDER = "—";

/**
 * Roles permitted to dismiss an alert (mirrors the BFF dismiss gate,
 * ADR-029 §7). The Alerts panel hides the Dismiss control for every other
 * role (fail-safe UX, CLAUDE.md §8.1); the BFF independently enforces the
 * same gate. Lives here (a non-component module) rather than in the panel
 * file so the panel stays fast-refresh-clean.
 */
export const ALERT_DISMISSER_ROLES: readonly Role[] = ["operator", "admin"];

/**
 * Flow-F severity bucket derived from the SAPIENT ``AlertPriority`` enum
 * (ADR-029 §5). Drives the badge colour bucket and the (deferred) Flow-F
 * toast-vs-banner surface.
 */
export type AlertSeverity = "info" | "warning" | "error" | "critical";

/**
 * The Flow-F surface an alert of a given severity *would* raise once the
 * notification subsystem exists (ADR-029 §5). **Not yet built** — see the
 * deferral hook in :func:`alerts/use-alerts-sse-bridge`. Carried on the
 * descriptor so the eventual dispatch keys off a single mapping.
 *
 *   - ``toast-auto`` — transient toast, auto-dismiss ~6 s (info / warning).
 *   - ``toast-sticky`` — toast that persists until the operator dismisses
 *     it (error / HIGH).
 *   - ``banner`` — full-width sticky banner, persists until any operator
 *     dismisses the alert (critical / CRITICAL).
 */
export type FlowFSurface = "toast-auto" | "toast-sticky" | "banner";

/** Presentation descriptor for a SAPIENT ``AlertPriority`` value. */
export interface AlertPriorityDescriptor {
  /** Operator-facing label (e.g., "High"). */
  readonly label: string;
  /** Flow-F severity bucket (ADR-029 §5). */
  readonly severity: AlertSeverity;
  /**
   * The would-be Flow-F notification surface for this severity (ADR-029
   * §5). The notification subsystem is deferred (PR5a inc 3 leaves a hook
   * only) — this field exists so the eventual dispatch reads one mapping.
   */
  readonly flowF: FlowFSurface;
  /**
   * The shared marker-status swatch this priority's badge renders with,
   * so alert badges reuse the Map's status palette (via ``getStatusColor``)
   * — REQ-UI-046, no invented hex.
   */
  readonly markerStatus: MarkerStatus;
}

const _BY_PRIORITY: Readonly<Record<number, AlertPriorityDescriptor>> = {
  0: { label: "Unspecified", severity: "info", flowF: "toast-auto", markerStatus: "unknown" },
  1: { label: "Low", severity: "info", flowF: "toast-auto", markerStatus: "unknown" },
  2: { label: "Medium", severity: "warning", flowF: "toast-auto", markerStatus: "degraded" },
  3: { label: "High", severity: "error", flowF: "toast-sticky", markerStatus: "unhealthy" },
  4: { label: "Critical", severity: "critical", flowF: "banner", markerStatus: "unhealthy" },
};

/**
 * Describe a SAPIENT ``AlertPriority`` integer for display (ADR-029 §5).
 * Unknown values (a future enum addition this UI predates) fall back to a
 * neutral ``info`` / grey-swatch descriptor labelled with the raw integer
 * — never throws, per the long-retention forward-compatibility discipline
 * (CLAUDE.md §8.3).
 */
export function describeAlertPriority(priority: number): AlertPriorityDescriptor {
  return (
    _BY_PRIORITY[priority] ?? {
      label: `Priority ${priority}`,
      severity: "info",
      flowF: "toast-auto",
      markerStatus: "unknown",
    }
  );
}

/**
 * The operator-facing labels for the SAPIENT ``AlertType`` enum
 * (``schemas/proto/meridian/v1/data/alert.proto`` §AlertType):
 * 0 UNSPECIFIED / 1 INFORMATION / 2 WARNING / 3 CRITICAL / 4 ERROR /
 * 5 FATAL / 6 MODE_CHANGE.
 */
const _ALERT_TYPE_LABELS: Readonly<Record<number, string>> = {
  0: "Unspecified",
  1: "Information",
  2: "Warning",
  3: "Critical",
  4: "Error",
  5: "Fatal",
  6: "Mode change",
};

/**
 * The operator-facing label for a SAPIENT ``AlertType`` integer. Unknown
 * values fall back to ``Type <n>`` (forward-compatibility, CLAUDE.md
 * §8.3) — never throws.
 */
export function alertTypeLabel(alertType: number): string {
  return _ALERT_TYPE_LABELS[alertType] ?? `Type ${alertType}`;
}

/**
 * Format an alert confidence in [0, 1] as a whole-percent string (e.g.,
 * ``0.95`` → ``"95%"``). Returns :data:`ALERT_DISPLAY_PLACEHOLDER` when
 * ``confidence`` is ``null`` (the source omitted it) or not a finite
 * number (defensive against a malformed payload that slipped the wire
 * guard). Rounds to the nearest integer percent.
 */
export function formatConfidence(confidence: number | null): string {
  if (confidence === null || !Number.isFinite(confidence)) {
    return ALERT_DISPLAY_PLACEHOLDER;
  }
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Format the age of an alert as a compact relative string (e.g.,
 * ``"just now"``, ``"5s"``, ``"3m"``, ``"2h"``, ``"1d"``) measured from
 * ``now`` (defaults to ``Date.now()``) back to the alert's
 * ``received_at``. Per ADR-029 §6.2 the age is live-updated in the row;
 * this is the pure formatter the row re-renders against.
 *
 * Returns :data:`ALERT_DISPLAY_PLACEHOLDER` for an unparseable timestamp.
 * Negative ages (a clock-skewed received_at in the near future) clamp to
 * ``"just now"`` — operator-legible over a misleading negative.
 */
export function formatAlertAge(receivedAt: string, now: number = Date.now()): string {
  const ts = Date.parse(receivedAt);
  if (Number.isNaN(ts)) {
    return ALERT_DISPLAY_PLACEHOLDER;
  }
  const deltaSec = Math.floor((now - ts) / 1000);
  if (deltaSec < 5) return "just now";
  if (deltaSec < 60) return `${deltaSec}s`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h`;
  return `${Math.floor(deltaHr / 24)}d`;
}
